import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "./SEO.jsx";
import ShareButtons from "./ShareButtons.jsx";
import NewsletterSignup from "./Newsletter.jsx";
import AdSlot from "./AdSlot.jsx";
import { AD_SLOTS } from "../lib/adsense.js";

// Keyword-front-loaded <title> tags for Google SERP (overrides the display H1
// only in the <title> meta — visible headline stays natural).
const SEO_TITLES = {
  "quanto-ganha-operario-japao-2026":
    "Quanto Ganha Operário no Japão em 2026 (Salário Real)",
  "vale-a-pena-voltar": "Vale a Pena Voltar do Japão para o Brasil?",
  "kakutei-shinkoku": "Kakutei Shinkoku: Como Declarar Imposto no Japão",
  "renovacao-visto-zairyu-card":
    "Renovar Visto e Zairyu Card no Japão (Guia 2026)",
  "trazer-familia-para-o-japao":
    "Como Trazer Família para o Japão: Visto de Dependente",
  "abrir-conta-banco-japao":
    "Como Abrir Conta no Banco no Japão (Yucho, Shinsei)",
  "encontrar-moradia-apato-japao":
    "Como Alugar Apartamento (Apato) no Japão Sendo Brasileiro",
  "hakken-vs-seishain": "Hakken vs Seishain: Qual Contrato Vale Mais no Japão",
  "shakai-vs-kokumin": "Shakai Hoken vs Kokumin Hoken: Qual é Melhor",
  "mandar-dinheiro-para-o-brasil":
    "Como Mandar Dinheiro do Japão para o Brasil (2026)",
  "gaimen-kirikae": "Gaimen Kirikae: Converter CNH Brasileira no Japão",
  "trabalhar-legal-japao-tipos-de-visto":
    "Tipos de Visto de Trabalho no Japão (Guia Completo)",
  "glossario-japones-portugues":
    "Glossário Japonês-Português para Brasileiros no Japão",
};

export default function GuideLayout({
  title,
  subtitle,
  slug,
  description,
  updated,
  readTime,
  children,
}) {
  const path = `/guias/${slug}`;
  return (
    <article>
      <SEO
        title={title}
        seoTitle={SEO_TITLES[slug]}
        description={description}
        path={path}
        type="article"
      />
      
      <Link
        to="/guias"
        className="inline-flex items-center gap-1.5 text-ink-soft text-sm mb-6 no-underline hover:text-ink transition-colors"
      >
        <ArrowLeft size={14} />
        Todos os guias
      </Link>

      <div className="text-[11px] tracking-[0.18em] uppercase text-accent mb-4 font-semibold">
        Guia
      </div>

      <h1 className="font-serif font-medium text-4xl md:text-5xl leading-[1.05] tracking-tight mb-4">
        {title}
      </h1>
      
      {subtitle && (
        <p className="text-lg md:text-xl text-ink-soft leading-relaxed mb-6 max-w-2xl">
          {subtitle}
        </p>
      )}

      <div className="flex items-center gap-4 text-xs text-ink-soft mb-6 font-mono">
        {updated && <span>Atualizado em {updated}</span>}
        {readTime && <span>· {readTime} de leitura</span>}
      </div>

      <div className="mb-10">
        <ShareButtons url={path} title={title} compact />
      </div>

      <div className="prose-content">
        {children}
      </div>

      <ShareButtons url={path} title={title} />

      <AdSlot slot={AD_SLOTS.guideInArticle} format="auto" />

      <NewsletterSignup />
    </article>
  );
}

// Typography components for use inside guides
export function H2({ children, id }) {
  return (
    <h2 
      id={id}
      className="font-serif font-medium text-3xl md:text-[34px] leading-tight tracking-tight mt-12 mb-4 scroll-mt-20"
    >
      {children}
    </h2>
  );
}

export function H3({ children, id }) {
  return (
    <h3
      id={id}
      className="font-serif font-medium text-2xl leading-tight tracking-tight mt-8 mb-3 scroll-mt-20"
    >
      {children}
    </h3>
  );
}

export function P({ children }) {
  return (
    <p className="text-[17px] leading-[1.7] text-ink mb-5">
      {children}
    </p>
  );
}

export function UL({ children }) {
  return (
    <ul className="text-[17px] leading-[1.7] text-ink mb-5 pl-6 list-disc space-y-2">
      {children}
    </ul>
  );
}

export function OL({ children }) {
  return (
    <ol className="text-[17px] leading-[1.7] text-ink mb-5 pl-6 list-decimal space-y-2 marker:text-accent marker:font-semibold">
      {children}
    </ol>
  );
}

export function Callout({ children, kind = "info" }) {
  const styles = {
    info: "bg-accent-soft border-accent",
    warn: "bg-yellow-50 border-yellow-400",
    success: "bg-green-50 border-success",
  };
  return (
    <div className={`my-6 rounded-xl border-l-4 px-5 py-4 ${styles[kind]}`}>
      <div className="text-[15px] leading-relaxed text-ink">{children}</div>
    </div>
  );
}

export function Quote({ children, source }) {
  return (
    <blockquote className="my-8 pl-5 border-l-2 border-accent">
      <p className="font-serif italic text-xl leading-snug text-ink">
        {children}
      </p>
      {source && (
        <cite className="block mt-2 text-sm not-italic text-ink-soft">
          — {source}
        </cite>
      )}
    </blockquote>
  );
}

export function ExternalLink({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent underline underline-offset-2 decoration-accent/40 hover:decoration-accent transition-colors"
    >
      {children}
    </a>
  );
}

export function Strong({ children }) {
  return <strong className="font-semibold text-ink">{children}</strong>;
}

export function Em({ children }) {
  return <em className="font-serif italic">{children}</em>;
}
